import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Search, XCircle, ChevronUp, ChevronDown } from "lucide-react";
import { Dentist } from "@/services/dentist-service";
import { statusLabels } from "@/utils/appointmentStatus";
import { formatISODate } from "@/utils/formatters";

export interface AppointmentFiltersData {
  dentistId?: string;
  startDate?: string;
  endDate?: string;
  appointmentStatus?: string;
  appointmentNumber?: string;
  pageSize?: number;
  patientName?: string;
  patientCPF?: string;
  patientPhone?: string;
}

interface AppointmentFiltersProps {
  dentists: Dentist[];
  onFilterChange: (filters: AppointmentFiltersData) => void;
}

export function AppointmentFilters({ dentists, onFilterChange }: AppointmentFiltersProps) {
  const [showFilters, setShowFilters] = useState(true);
  const [dentistId, setDentistId] = useState<string>();
  const [startDate, setStartDate] = useState<string>(formatISODate(new Date()));
  const [endDate, setEndDate] = useState<string>(formatISODate(new Date()));
  const [appointmentStatus, setAppointmentStatus] = useState<string>();
  const [appointmentNumber, setAppointmentNumber] = useState<string>();
  const [pageSize, setPageSize] = useState<string>("100");
  const [patientName, setPatientName] = useState("");
  const [patientCPF, setPatientCPF] = useState("");
  const [patientPhone, setPatientPhone] = useState("");

  const handleNumericInput = (value: string, setter: (v: string) => void) => {
    setter(value.replace(/[^0-9]/g, ""));
  };

  const handleFilter = () => {
    onFilterChange({
      dentistId: dentistId === "all" ? undefined : dentistId,
      startDate,
      endDate,
      appointmentStatus: appointmentStatus === "all" ? undefined : appointmentStatus,
      appointmentNumber,
      pageSize: parseInt(pageSize),
      patientName,
      patientCPF,
      patientPhone,
    });
  };

  const handleClearFilters = () => {
    setDentistId(undefined);
    setStartDate(formatISODate(new Date()));
    setEndDate(formatISODate(new Date()));
    setAppointmentStatus(undefined);
    setAppointmentNumber(undefined);
    setPageSize("100");
    setPatientName("");
    setPatientCPF("");
    setPatientPhone("");
    onFilterChange({});
  };

  return (
    <div className="space-y-2">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setShowFilters(!showFilters)}
        className="filter-toggle"
      >
        {showFilters ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        {showFilters ? "Esconder Filtros" : "Mostrar Filtros"}
      </Button>

      {showFilters && (
        <div className="filter-box">

          {/* LINHA 1: Dentista, Status, Registros */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <div className="space-y-1">
              <Label className="filter-label">Dentista</Label>
              <Select value={dentistId} onValueChange={setDentistId}>
                <SelectTrigger className="filter-input">
                  <SelectValue placeholder="Todos os dentistas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os Dentistas</SelectItem>
                  {dentists.map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label className="filter-label">Status</Label>
              <Select value={appointmentStatus} onValueChange={setAppointmentStatus}>
                <SelectTrigger className="filter-input">
                  <SelectValue placeholder="Todos os status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os Status</SelectItem>
                  {Object.entries(statusLabels).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label className="filter-label">Registros por Página</Label>
              <Select value={pageSize} onValueChange={setPageSize}>
                <SelectTrigger className="filter-input">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="20">20 por página</SelectItem>
                  <SelectItem value="50">50 por página</SelectItem>
                  <SelectItem value="100">100 por página</SelectItem>
                  <SelectItem value="200">200 por página</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* LINHA 2: Busca por Paciente e ID */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
            <div className="space-y-1">
              <Label className="filter-label">Nome do Paciente</Label>
              <Input placeholder="Parte do nome..." value={patientName}
                onChange={(e) => setPatientName(e.target.value)} className="filter-input" />
            </div>

            <div className="space-y-1">
              <div className="flex items-center gap-1.5">
                <Label className="filter-label font-semibold">CPF</Label>
                <span className="text-[10px] font-mono text-muted-foreground italic">(somente números)</span>
              </div>
              <Input placeholder="parte do CPF" value={patientCPF}
                onChange={(e) => handleNumericInput(e.target.value, setPatientCPF)}
                className="filter-input" inputMode="numeric" />
            </div>

            <div className="space-y-1">
              <div className="flex items-center gap-1.5">
                <Label className="filter-label font-semibold">Celular</Label>
                <span className="text-[10px] font-mono text-muted-foreground italic">(somente números)</span>
              </div>
              <Input placeholder="parte do celular" value={patientPhone}
                onChange={(e) => handleNumericInput(e.target.value, setPatientPhone)}
                className="filter-input" inputMode="numeric" />
            </div>

            <div className="space-y-1">
              <Label className="filter-label">ID Atendimento</Label>
              <Input placeholder="Ex: 12345" value={appointmentNumber || ""}
                onChange={(e) => setAppointmentNumber(e.target.value)} className="filter-input" />
            </div>
          </div>

          {/* LINHA 3: Datas e Botões */}
          <div className="flex flex-wrap items-end gap-2 border-t pt-2">
            <div className="space-y-1 w-full md:w-36">
              <Label className="filter-label">Data Início</Label>
              <Input type="date" value={startDate}
                onChange={(e) => setStartDate(e.target.value)} className="filter-input" />
            </div>
            <div className="space-y-1 w-full md:w-36">
              <Label className="filter-label">Data Fim</Label>
              <Input type="date" value={endDate}
                onChange={(e) => setEndDate(e.target.value)} className="filter-input" />
            </div>
            <div className="flex-1" />
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={handleClearFilters} className="gap-1.5 h-7 text-xs">
                <XCircle className="h-3 w-3" /> Limpar
              </Button>
              <Button size="sm" onClick={handleFilter} className="bg-cyan-600 hover:bg-cyan-700 text-white gap-1.5 h-7 text-xs">
                <Search className="h-3 w-3" /> Filtrar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}